<template>
  <div class="container">
    <div class="form-header">
      <h1 class="title-screen">Finance info</h1>
      <div v-if="userRole === 'OWNER'" class="edit" @click="toggleEditing()">
        <img src="/assets/edit-pen.svg" alt="Edit icon" class="edit-pen" />
        <p class="edit-text">EDIT</p>
      </div>
    </div>
    <p class="finance-description">Your finance info is applied only on events organized by you.</p>
    <div class="inputs">
      <text-field
        v-model.trim="form.legalName"
        label="Organizer legal name*"
        :disabled="!enableEditing"
        :validator="$v.form.legalName"
      />
      <text-field
        v-model.trim="form.OIB"
        label="OIB*"
        :disabled="!enableEditing"
        :validator="$v.form.OIB"
      />
      <text-field 
        v-model.trim="form.authorizedPerson" 
        label="Name and surname of the authorized person" 
        :disabled="!enableEditing"
        :validator="$v.form.authorizedPerson" 
      />
      <text-field
        v-model.trim="form.address"
        label="Address*"
        :disabled="!enableEditing"
        :validator="$v.form.address"
      />
      <div style="display: flex; gap: 1rem">
        <text-field
          v-model.trim="form.city"
          label="City*"
          :disabled="!enableEditing"
          :validator="$v.form.city"
        />
        <text-field
          v-model.trim="form.postalCode"
          label="Postal code*"
          :disabled="!enableEditing"
          :validator="$v.form.postalCode"
        />
      </div>
      <text-field
        v-model.trim="form.IBAN"
        label="IBAN"
        :disabled="!enableEditing"
        :validator="$v.form.IBAN"
      />
      <text-field 
        v-model.trim="form.primaryEmail" 
        label="Organizer primary email" 
        :disabled="!enableEditing"
        :validator="$v.form.primaryEmail" 
      />
      <text-field 
        v-model.trim="form.contactNumber" 
        label="Phone number"
        :disabled="!enableEditing"
        :validator="$v.form.contactNumber"
      />
      <div class="radio-buttons">
        <p class="vat-text">U sustavu PDV-a</p>
        <div class="radio-item">
          <input
            id="no"
            v-model="form.VATObligor"
            type="radio"
            :validator="$v.form.VATObligor"
            :value="false"
            :disabled="!enableEditing"
          />
          <label for="no">Ne</label>
        </div>
        <div class="radio-item">
          <input
            id="yes"
            v-model="form.VATObligor"
            :validator="$v.form.VATObligor"
            type="radio"
            :value="true"
            :disabled="!enableEditing"
          />
          <label for="yes">Da</label>
        </div>
      </div>
    </div>
    <main-button
      text="Save"
      depressed
      class="button"
      :disabled="!enableEditing || organizationHasActiveEvents"
      @click="submitForm"
    />
    <p v-if="enableEditing && organizationHasActiveEvents">
      This is sensitive info that is subject of fiscalisation and cannot be change when you have
      active sales for events. Please contact CoreEvent to make changes.
    </p>
    <simple-snackbar v-bind.sync="snackbar" />
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'
import { omit } from 'lodash'
import moment from 'moment'

import { GET_ORGANIZATION, UPDATE_ORGANIZATION } from '../../graphql/organization.graphql'
import { submitGuard } from './../../utils'
import SimpleSnackbar from '../shared/snackbars/simple-snackbar.vue'

export default {
  name: 'finance-info',
  components: { SimpleSnackbar },
  props: {
    initialValues: {
      type: Object,
      default: () => {},
    },
    vs: {
      type: Object,
      default: () => {},
    },
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      enableEditing: false,
      snackbar: {},
      form: {},
      userRole: null,
      organizationHasActiveEvents: false,
    }
  },
  validations() {
    return {
      form: {
        legalName: { required },
        OIB: { required },
        authorizedPerson: { required },
        IBAN: { required },
        address: { required },
        city: { required },
        postalCode: { required },
        VATObligor: { required },
        primaryEmail: { required, email },
        contactNumber: { required },
      },
    }
  },
  methods: {
    toggleEditing() {
      this.enableEditing = !this.enableEditing
    },
    async submitForm() {
      if (this.$v.$invalid) {
        this.snackbar = {
          open: true,
          messageProp: 'Fill in the required information.',
          color: '#DB384C',
        }
      }
      submitGuard(this, async () => {
        const organizationInfo = this.form
        organizationInfo.VATPercentage = this.form.VATObligor ? 5.0 : 0.0

        const { errors } = await this.$apollo.mutate({
          mutation: UPDATE_ORGANIZATION,
          variables: {
            organizationId: this.form.id,
            organizationInfo: omit(organizationInfo, 'id'),
          },
          refetchQueries: [
            { query: GET_ORGANIZATION, variables: { organizationSlug: this.organizationSlug } },
          ],
        })

        this.snackbar = {
          open: true,
          messageProp: errors ? 'Something went wrong. Please try again.' : 'Finance info saved.',
          color: errors ? 'error' : 'primary',
        }
        this.toggleEditing()
      })
    },
  },
  apollo: {
    getOrganization: {
      query: GET_ORGANIZATION,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading && data) {
          const { events, ...rest } = data.getOrganization
          this.organizationHasActiveEvents = events.some(e => moment(e.to).isAfter(moment()))
          this.userRole = rest.userOrganization.role
          this.form = omit(rest, ['liveEventsCount', 'userOrganization'])
        }
      },
    },
  },
}
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  padding: 0 !important;
  margin: 0 !important;
  max-width: 43.75rem;
}
.organizer-name {
  display: flex;
  flex-wrap: wrap;
  column-gap: 1.5rem;
}
.organizer-info {
  display: flex;
  flex-wrap: wrap;
  column-gap: 1.5rem;
}
.links {
  display: flex;
  column-gap: 1.5rem;
  flex-wrap: wrap;
}
.button {
  width: 6.25rem;
  align-self: flex-end;
}
.vat-text {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.125rem;
  color: #333333;
}
.radio-item {
  display: inline-block;
  position: relative;
  padding: 0 0.375rem;
  margin: 0.625rem 0 0;
}

.radio-item input[type='radio'] {
  display: none;
}

.radio-item label {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1.125rem;
  color: #979797;
}

.radio-item label:before {
  content: ' ';
  display: inline-block;
  position: relative;
  top: 0.313rem;
  margin: 0 0.313rem 0 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 0.688rem;
  border: 0.125rem solid #666666;
  background-color: transparent;
}

.radio-item input[type='radio']:checked + label:after {
  border-radius: 0.688rem;
  width: 0.75rem;
  height: 0.75rem;
  position: absolute;
  top: 0.563rem;
  left: 0.625rem;
  content: ' ';
  display: block;
  background: #0277bd;
}
.form-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  color: #ff4c00;
}
.finance-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
}
.edit-text {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 1.125rem;
  color: #0277bd;
  margin-bottom: 0;
}
.edit {
  display: flex;
  align-items: center;
  cursor: pointer;
}
.edit-pen {
  height: 1.125rem;
  width: 1.125rem;
  margin-right: 0.625rem;
}
.form-header {
  display: flex;
  justify-content: space-between;
}
.inputs {
  display: flex;
  flex-direction: column;
  row-gap: 2rem;
}
.finance-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  color: #979797;
}
</style>
